/**
 * Een volgordesleutel: een plaats in een lijst die niet verschuift als er elders iets bijkomt of
 * weggaat (R-40, R-72, besluit van 31 oktober 2026, ontwerp 6.8).
 *
 * **Waarom een sleutel en niet de plaats in de `Y.Array`.** Yjs kan een element in een lijst niet
 * verplaatsen. Weghalen en opnieuw invoegen kan alleen met een kopie, en een kopie is een ander
 * Yjs-item: wat een medeschrijver op hetzelfde moment in de oude rij typte, landt in een rij die er
 * niet meer is (stil dataverlies), en de attributie en de ongedaan-maken-stapel van de tekst in de
 * rij gaan mee verloren. Daarom blijft de rij liggen en krijgt ze een sleutel, en is de volgorde
 * die van de sleutels. Twee schrijvers die tegelijk verplaatsen raken elkaar dan op één sleutel per
 * rij, en daar wint de laatste; er verdwijnt niets.
 *
 * **De vorm.** Een breuk achter de komma in grondtal 62, geschreven als tekens die in ASCII oplopen
 * (`0-9A-Za-z`), zodat een gewone stringvergelijking de volgorde geeft en elke taal en elke client
 * hetzelfde sorteert. Een sleutel eindigt nooit op `0`: dan is er tussen twee sleutels altijd een
 * derde. Het algoritme is de middenwaarde van David Greenspan ("Implementing Fractional Indexing",
 * observablehq.com/@dgreensp/implementing-fractional-indexing), zonder het gehele deel: een
 * herhaalgroep heeft geen miljoenen rijen.
 */

const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
const KEY = /^[0-9A-Za-z]*[1-9A-Za-z]$/

/** Is dit een geldige volgordesleutel? Wat een client anders in een rij zet, telt als geen sleutel. */
export function isOrderKey(value: unknown): value is string {
  return typeof value === 'string' && KEY.test(value)
}

function digit(character: string | undefined): number {
  return character === undefined ? 0 : DIGITS.indexOf(character)
}

/** De middenwaarde tussen `low` en `high`; `low` mag leeg zijn, `high` ontbreekt voor "het eind". */
function midpoint(low: string, high: string | undefined): string {
  if (high !== undefined) {
    // Het gemeenschappelijke begin blijft staan; daarachter verder zoeken.
    let common = 0
    while ((low[common] ?? '0') === high[common]) common += 1
    if (common > 0) return high.slice(0, common) + midpoint(low.slice(common), high.slice(common))
  }
  const lowDigit = low === '' ? 0 : digit(low[0])
  const highDigit = high === undefined ? DIGITS.length : digit(high[0])
  if (highDigit - lowDigit > 1) return DIGITS[Math.round((lowDigit + highDigit) / 2)] ?? ''
  // Aangrenzende cijfers: een cijfer dieper.
  if (high !== undefined && high.length > 1) return high.slice(0, 1)
  return (DIGITS[lowDigit] ?? '') + midpoint(low.slice(1), undefined)
}

/**
 * Een sleutel strikt tussen `low` en `high`. Zonder `low` is het vóór alles, zonder `high` na alles.
 * Wie twee sleutels in de verkeerde volgorde of twee gelijke meegeeft, krijgt een fout: dat is een
 * fout van de aanroeper, die eerst opnieuw nummert (`renumber` in `sectionDocument.ts`).
 */
export function keyBetween(low: string | undefined, high: string | undefined): string {
  if (low !== undefined && high !== undefined && low >= high) throw new Error(`geen sleutel tussen ${low} en ${high}`)
  if (high === undefined && low !== undefined) return after(low)
  return midpoint(low ?? '', high)
}

/**
 * Een sleutel na `low`, voor achteraan: het eerste cijfer dat nog omhoog kan, één omhoog.
 *
 * Niet de middenwaarde naar het eind: die halveert de ruimte bij elke rij en groeit met een cijfer
 * per zes rijen, en achteraan toevoegen is het gewone geval. Zo groeit hij met een cijfer per
 * ongeveer dertig rijen.
 */
function after(low: string): string {
  for (let place = 0; place < low.length; place += 1) {
    const value = digit(low[place])
    if (value < DIGITS.length - 1) return low.slice(0, place) + (DIGITS[value + 1] ?? '')
  }
  return `${low}V`
}
